import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { RequestCreateDatasetVersion } from '@/schema'
import { SortOrder } from '@/types/common'
import { convertSortToApiFormat } from '@/utils'
import { DEFAULT_PAGE, TABLE_PAGE_SIZE } from '@/utils/constants'

import {
  DATASET_VERSION_LIST_QUERY_KEY,
  QUERY_KEY_DATASET_VERSION_LIST,
} from './constants'
import { datasetVersionApi } from './services'
import { DatasetVersionFilters } from './types'

const invalidateDatasetVersionQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  projectId?: string
) => {
  queryClient.invalidateQueries({
    queryKey: [QUERY_KEY_DATASET_VERSION_LIST],
    refetchType: 'active',
  })

  // Invalidate specific deliverable set detail if provided
  if (projectId) {
    queryClient.invalidateQueries({
      queryKey: DATASET_VERSION_LIST_QUERY_KEY([projectId]),
      refetchType: 'active',
    })
  }
}

export const useDatasetVersionList = (params: {
  search?: string
  page?: number
  rowsPerPage?: number
  sort?: {
    column: string
    direction: SortOrder
  }
  filters?: DatasetVersionFilters
  enabled?: boolean
}) => {
  const {
    search = '',
    page = DEFAULT_PAGE,
    rowsPerPage = TABLE_PAGE_SIZE,
    sort,
    filters,
    enabled = true,
  } = params
  const query = search.trim() || ''
  const sortBy = convertSortToApiFormat(
    sort || { column: null, direction: null },
    '+name'
  )
  const filterBy: Record<string, string[]> = {}
  if (filters?.projectId) {
    filterBy.project_id = [filters.projectId]
  }
  if (filters?.provider) {
    filterBy.provider = [filters.provider]
  }

  return useQuery({
    queryKey: DATASET_VERSION_LIST_QUERY_KEY([
      query,
      page,
      rowsPerPage,
      sortBy,
      JSON.stringify(filters || {}),
    ]),
    queryFn: async () => {
      const { data, pagination } =
        await datasetVersionApi.getDatasetVersionList({
          q: query,
          offset: page * rowsPerPage,
          limit: rowsPerPage,
          sort_by: sortBy,
          filter_by: Object.keys(filterBy).length > 0 ? filterBy : undefined,
        })
      return { data, pagination }
    },
    staleTime: 0,
    enabled,
  })
}

export const useCreateDatasetVersion = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: RequestCreateDatasetVersion) => {
      const response = await datasetVersionApi.createDatasetVersion(data)
      return response
    },
    onSuccess: (response, variables) => {
      const projectId = response.data?.[0]?.project_id || variables.project_id
      invalidateDatasetVersionQueries(queryClient, projectId)
    },
  })
}
